import { useAtom, useAtomValue } from 'jotai'
import {
  bestOffersAtom,
  buyTokenAtom,
  sellTokenAtom,
  skipOfferToTakeIdsAtom,
} from '../../stores/trade-store'
import { calcOfferPrice } from 'src/common/utils/offers/offerPrices'
import { OfferWithTake } from 'src/common/utils/offers/findBestOffers'
import Checkbox from 'src/common/ui-kit/Checkbox/Checkbox'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  formatBuyTokenAmountMulti,
  formatBuyTokenAmountSingle,
  formatSellTokenAmountMulti,
} from '../SwapTrade/utils'
import { displayAmount } from 'src/common/utils/formatters/number'

interface RowProps {
  offer: OfferWithTake
  checked: boolean
  onChange: (v: boolean, offer: OfferWithTake) => void
  gridTemplateColumns: string
}

const Row = ({ offer, checked, onChange, gridTemplateColumns }: RowProps) => {
  const buyPrice = calcOfferPrice(offer, 0)
  const buyTokenToTake = formatBuyTokenAmountSingle(offer)

  return (
    <div
      className='grid gap-2 typo-label1'
      key={offer.id}
      style={{
        gridTemplateColumns,
      }}
    >
      <Checkbox checked={checked} onChange={value => onChange(value, offer)} />
      <div>{displayAmount(buyTokenToTake)}</div>
      <div>{displayAmount(buyPrice)}</div>
      <div>{displayAmount(offer.toTake)}</div>
    </div>
  )
}

const OffersToTake = ({ className }: { className?: string }) => {
  const { t } = useTranslation('trade')

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const [skipOfferToTakeIds, setSkipOfferToTakeIds] = useAtom(
    skipOfferToTakeIdsAtom
  )
  const [checkboxValue, setCheckboxValue] = useState(!skipOfferToTakeIds.length)
  const bestOffers = useAtomValue(bestOffersAtom)
  const offersToTake = bestOffers.filter(
    offer => !skipOfferToTakeIds.includes(offer.id)
  )
  const totalToSell = formatSellTokenAmountMulti(offersToTake)
  const totalToBuy = formatBuyTokenAmountMulti(offersToTake)

  const gridTemplateColumns = '20px 1fr 1fr 1fr'

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      setCheckboxValue(checked)
      setSkipOfferToTakeIds([])
    }
  }

  const handleOfferCheckChange = (checked: boolean, offer: OfferWithTake) => {
    if (checked) {
      setSkipOfferToTakeIds(skipOfferToTakeIds.filter(id => id !== offer.id))
    } else {
      setCheckboxValue(false)
      setSkipOfferToTakeIds([...skipOfferToTakeIds, offer.id])
    }
  }

  return (
    <div>
      <div className='text-main-accent mb-4'>Offers to take:</div>
      <div className={`flex flex-col gap-2 ${className}`}>
        <div
          className='grid gap-2'
          style={{
            gridTemplateColumns,
          }}
        >
          <Checkbox checked={checkboxValue} onChange={handleCheckboxChange} />
          <div className='typo-label1'>
            {t('offersToTake.amount')} {buyToken?.symbol}
          </div>
          <div className='typo-label1'>
            {t('offersToTake.price')} {sellToken?.symbol}
          </div>
          <div className='typo-label1'>
            {t('offersToTake.amount')} {sellToken?.symbol}
          </div>
        </div>
        {bestOffers.map(offer => (
          <Row
            key={offer.id}
            offer={offer}
            checked={!skipOfferToTakeIds.includes(offer.id)}
            gridTemplateColumns={gridTemplateColumns}
            onChange={handleOfferCheckChange}
          />
        ))}
        <hr />
        <div
          className='grid gap-2'
          style={{
            gridTemplateColumns,
          }}
        >
          <div></div>
          <div className='typo-label1'>
            {displayAmount(totalToBuy, { maxDecimals: 4 })}
          </div>
          <div></div>
          <div className='typo-label1'>
            {displayAmount(totalToSell, { maxDecimals: 4 })}
          </div>
          <div></div>
        </div>
      </div>
    </div>
  )
}

export default OffersToTake
